import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetCurrentSeason } from '../api/seasonsApi';
import { AuthContext } from './AuthContext';
import { useGetLeagueBySlug, useGetUserLeagues } from '../api/leaguesApi';

const ActiveLeagueContext = createContext(null);

export const ActiveLeagueProvider = ({ children }) => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const leagueIdParam = searchParams.get('leagueId');
  const { user, loading: authLoading } = useContext(AuthContext);
  // Initialise with a lightweight stub so slug is immediately available to consumers
  const [activeLeague, setActiveLeagueState] = useState(
    slug ? { slug } : (leagueIdParam ? { _id: leagueIdParam } : null)
  );

  // Now that activeLeague is defined, we can safely fetch current season
  const { data: seasonData, isLoading: seasonLoading } = useGetCurrentSeason(activeLeague?._id);
  
  // Fetch leagues list once user is authenticated
  const { data: fetchedLeagues, isLoading: leaguesFetching, error: leaguesFetchError } = useGetUserLeagues(!!user);

  // Memoized so an empty list keeps a stable identity across renders. Both
  // effects below depend on `leagues`; a fresh [] every render would re-run
  // the no-slug null-out effect against the fallback seeding effect forever.
  const leagues = useMemo(() => (
    fetchedLeagues && Array.isArray(fetchedLeagues) && fetchedLeagues.length > 0
      ? fetchedLeagues
      : (user?.leagues || [])
  ), [fetchedLeagues, user]);

  const leaguesLoading = authLoading || leaguesFetching;
  const listedLeague = slug ? leagues.find(league => league.slug === slug) : null;
  const shouldFetchLeagueBySlug = Boolean(
    user && slug && !leaguesLoading && !listedLeague
  );
  const {
    data: leagueBySlugResponse,
    isLoading: leagueBySlugLoading,
    error: leagueBySlugError
  } = useGetLeagueBySlug(slug, shouldFetchLeagueBySlug);
  const leagueBySlug = leagueBySlugResponse?.data?.league || null;
  const combinedLoading = (
    leaguesLoading ||
    seasonLoading ||
    (shouldFetchLeagueBySlug && leagueBySlugLoading)
  );
  const leaguesError = leaguesFetchError || leagueBySlugError || null



  useEffect(() => {
    // If there is no slug in the URL -> definitely no active league
    if (!slug) {
      setActiveLeagueState(null);
      return;
    }

    // If leagues are still loading, keep the previous activeLeague (prevents flicker)
    if (leaguesLoading) {
      return;
    }

    const hiddenMemberLeague = (
      leagueBySlug?.slug === slug &&
      leagueBySlug?.isUserMember === true
    )
      ? leagueBySlug
      : null;
    const foundLeague = listedLeague || hiddenMemberLeague;

    if (!foundLeague && shouldFetchLeagueBySlug && leagueBySlugLoading) {
      return;
    }
    
    console.log(`ActiveLeagueContext: Looking for slug "${slug}" in ${leagues.length} leagues`);
    console.log('ActiveLeagueContext: Available league slugs:', leagues.map(l => l.slug));
    console.log('ActiveLeagueContext: Available leagues full data:', leagues.map(l => ({ slug: l.slug, _id: l._id, name: l.name })));
    console.log('ActiveLeagueContext: Found league:', foundLeague ? foundLeague.name : 'NOT FOUND');
    
    // If not found, suggest using leagueId parameter as fallback
    if (!foundLeague && leagueIdParam) {
      console.log(`ActiveLeagueContext: Slug not found, but leagueId param available: ${leagueIdParam}`);
    }
    
    // Only update if the found league differs from the current one
    if (foundLeague && (activeLeague?._id !== foundLeague._id)) {
      setActiveLeagueState(foundLeague);
    } else if (!foundLeague && leagueIdParam) {
      // If slug not found but leagueId param exists, try to find by ID
      const foundById = leagues.find(l => (l._id ?? l.id) === leagueIdParam);
      if (foundById && (activeLeague?._id !== foundById._id)) {
        console.log(`ActiveLeagueContext: Using leagueId fallback, found league: ${foundById.name}`);
        setActiveLeagueState(foundById);
      }
    }
    // If no league matched, keep previous state (don't null-out) so consumers continue to have slug

  // activeLeague is intentionally NOT a dependency here. On pages without a
  // slug this effect nulls it while the last-browsed fallback effect below
  // repopulates it, so reacting to activeLeague changes would make the two
  // effects ping-pong forever (render loop + request storm in the header).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slug,
    leagueIdParam,
    leagues,
    leaguesLoading,
    listedLeague,
    leagueBySlug,
    leagueBySlugLoading,
    shouldFetchLeagueBySlug
  ]);

  // Fallback: if slug missing but ?leagueId= present, derive league
  useEffect(() => {
    if (slug || !leagueIdParam || !leagues || leagues.length === 0) return;
    const foundById = leagues.find(l => (l._id ?? l.id) === leagueIdParam);
    if (foundById && activeLeague?._id !== foundById._id) {
      setActiveLeagueState(foundById);
    }
  }, [slug, leagueIdParam, leagues, activeLeague]);

  // NEW FALLBACK: If there is NO slug in the URL and NO leagueId query param, but the user
  // does belong to one or more leagues, automatically select a league so that
  // header navigation ("My League", "Transfers", etc.) can use its slug immediately after
  // a successful join. Prefer the league the user was last browsing (persisted whenever a
  // league page is visited) — otherwise on pages like /onboarding the header would silently
  // switch to an arbitrary league (leagues[0] is the newest league, not the current one).
  useEffect(() => {
    if (slug || leagueIdParam) return; // A specific league is already implied by the URL
    if (activeLeague || !Array.isArray(leagues)) return;

    let storedSlug = null;
    try {
      storedSlug = localStorage.getItem('lastActiveLeagueSlug');
    } catch (e) {
      // In some environments (private mode) localStorage may throw
    }

    if (leagues.length > 0) {
      const lastBrowsedLeague = storedSlug ? leagues.find(l => l.slug === storedSlug) : null;
      setActiveLeagueState(lastBrowsedLeague || leagues[0]);
      return;
    }

    // Quick Play leagues are intentionally hidden from the member listing, so an
    // empty list cannot rule out an active Quick Play run. Once the list has
    // settled, restore the run from the persisted slug as a slug-only stub, the
    // same shape slug routes start from; slug-first consumers (teams query,
    // header nav, quickplay label heuristic) all work off it. Regular league
    // slugs stay excluded because for them the empty member list is authoritative.
    if (!leaguesLoading && storedSlug && storedSlug.startsWith('quick-play-')) {
      setActiveLeagueState({ slug: storedSlug });
    }
  }, [slug, leagueIdParam, leagues, activeLeague, leaguesLoading]);

  // The plan mentions setActiveLeague in step 1.1 and 3.2.
  // In a slug-driven system, setActiveLeague would typically trigger navigation.
  // For now, providing a direct setter. Its usage in SwitchBlock.jsx (3.2) will clarify its role.
  // If it's purely for navigation, it might be better to expose navigate function or similar.
  const contextValue = useMemo(() => ({
    activeLeague,
    season: seasonData?.season ?? null,
    setActiveLeague: setActiveLeagueState,
    isLoading: combinedLoading,
    error: leaguesError,
  }), [activeLeague, combinedLoading, leaguesError, seasonData]);

  // Persist the last active league slug for navigation purposes (e.g., Join-League back button)
  useEffect(() => {
    if (activeLeague?.slug && slug) {
      // Only update localStorage if we have a URL slug (not just a fallback league)
      // This prevents stale providers from overwriting with old values
      try {
        console.log(`ActiveLeagueContext: Setting lastActiveLeagueSlug to "${activeLeague.slug}" (from URL: ${slug})`);
        localStorage.setItem('lastActiveLeagueSlug', activeLeague.slug);
      } catch (e) {
        // In some environments (private mode) localStorage may throw
        console.warn('ActiveLeagueContext: Unable to persist lastActiveLeagueSlug', e);
      }
    }
  }, [activeLeague?.slug, slug]);

  return (
    <ActiveLeagueContext.Provider value={contextValue}>
      {children}
    </ActiveLeagueContext.Provider>
  );
};

export const useActiveLeagueContext = () => {
  const context = useContext(ActiveLeagueContext);
  if (context === undefined || context === null) {
    throw new Error('useActiveLeagueContext must be used within an ActiveLeagueProvider');
  }
  return context;
};
